import React, { useState, useRef, useLayoutEffect, useEffect } from "react";
import { createPortal } from "react-dom";

const CustomDropdown = ({ value, onChange, options, placeholder, label, disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const selectedOption = options.find((opt) => opt.id === value);

  const updateCoords = () => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setCoords({ top: r.bottom + 4, left: r.left, width: r.width });
  };

  useLayoutEffect(() => {
    if (isOpen) updateCoords();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || disabled) return;
    const reposition = () => updateCoords();
    const onDocDown = (e) => {
      if (btnRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setIsOpen(false);
    };
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    document.addEventListener("mousedown", onDocDown);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
      document.removeEventListener("mousedown", onDocDown);
    };
  }, [isOpen, disabled]);

  return (
    <div className="relative">
      {label && (
        <label className="block text-xs font-semibold text-gray-600 mb-2">
          {typeof label === "string" ? label : <>{label}</>}
        </label>
      )}
      <button
        ref={btnRef}
        type="button"
        disabled={disabled}
        onClick={() => {
          if (!disabled) setIsOpen((o) => !o);
        }}
        className={`w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-left text-gray-900 bg-white flex items-center justify-between transition-colors ${
          disabled
            ? "opacity-50 bg-gray-100 cursor-not-allowed"
            : "hover:border-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer"
        }`}
      >
        <span className={selectedOption ? "text-gray-900" : "text-gray-500"}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <i
          className={`fa-solid fa-chevron-down text-xs text-gray-400 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
        ></i>
      </button>

      {isOpen && !disabled &&
        coords &&
        createPortal(
          <div
            ref={menuRef}
            style={{
              position: "fixed",
              top: coords.top,
              left: coords.left,
              width: coords.width,
              zIndex: 10000,
            }}
            className="bg-white border border-gray-300 rounded-lg shadow-xl max-h-40 overflow-y-auto"
          >
            {options.map((option) => (
              <button
                key={option.id}
                type="button"
                onClick={() => {
                  onChange(option.id);
                  setIsOpen(false);
                }}
                className={`w-full px-4 py-3 text-left text-sm border-none hover:bg-blue-50 transition-colors flex justify-between items-center bg-white ${
                  value === option.id
                    ? "bg-blue-50 text-blue-600 font-semibold"
                    : "text-gray-900"
                }`}
              >
                <span>{option.label}</span>
                {option.price && (
                  <span className="text-gray-600 text-xs">{option.price}</span>
                )}
              </button>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
};

export default CustomDropdown;
